import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import PickItem from '../PickItem';
import styles from './MobileWatchList.module.css';
import { useGameContext } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';

const MobileWatchList = ({ week, games, isOpen, onClose }) => {
  const { user } = useAuth();
  const {
    watchedPicks,
    toggleWatchPick,
    clearWatchesForWeek,
    requestDirectLock,
    getMaxPicksForWeek,
    getLocksForWeek,
    currentWeek,
    isFutureWeekLockAllowed,
  } = useGameContext();

  const [futureWeekLockAllowed, setFutureWeekLockAllowed] = useState(true);

  useEffect(() => {
    const checkFutureWeekLock = async () => {
      try {
        const allowed = await isFutureWeekLockAllowed(week);
        setFutureWeekLockAllowed(allowed);
      } catch {
        setFutureWeekLockAllowed(false);
      }
    };
    checkFutureWeekLock();
  }, [week, isFutureWeekLockAllowed]);

  const watchesForWeek = useMemo(
    () => watchedPicks.filter((pick) => pick.weekNumber === week && pick.pickType !== 'bye'),
    [watchedPicks, week]
  );

  const enrichedWatches = useMemo(() => {
    return watchesForWeek.map((pick) => {
      const game = games.find((g) => g.id === pick.gameId);
      if (!game) return pick;
      const homeTeam = typeof game.home_team === 'object' ? game.home_team.name : game.home_team;
      const awayTeam = typeof game.away_team === 'object' ? game.away_team.name : game.away_team;
      const isSpread = pick.pickType === 'home' || pick.pickType === 'away';
      return {
        ...pick,
        team: pick.pickType === 'home' ? homeTeam : pick.pickType === 'away' ? awayTeam : pick.pickType,
        opponent: pick.pickType === 'home' ? awayTeam : pick.pickType === 'away' ? homeTeam : '',
        gameDate: game.start_date,
        liveLine: pick.liveLine ?? (isSpread ? game.betting?.spread : game.betting?.over_under),
      };
    });
  }, [watchesForWeek, games]);

  const userLocksCount = useMemo(() => {
    const locksForWeek = getLocksForWeek(week);
    return locksForWeek.filter(
      (lock) =>
        lock.userId === user?.id &&
        (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;
  }, [getLocksForWeek, week, user?.id]);

  const maxPicks = getMaxPicksForWeek(week);
  const hasReachedMaxPicks = userLocksCount >= maxPicks;

  const handleRemove = useCallback(
    (pick) => {
      toggleWatchPick(pick.gameId, pick.pickType, week, 0, 0, true);
    },
    [toggleWatchPick, week]
  );

  const handleLock = useCallback(
    (pick) => {
      requestDirectLock(pick, week, { onSuccess: onClose });
    },
    [requestDirectLock, week, onClose]
  );

  const handleClearAll = useCallback(() => {
    if (watchesForWeek.length === 0) return;
    clearWatchesForWeek(week);
  }, [clearWatchesForWeek, week, watchesForWeek.length]);

  if (!isOpen) return null;

  return (
    <>
      <button
        type="button"
        className={styles.backdrop}
        onClick={onClose}
        aria-label="Close watch list"
      />
      <div className={styles.sheet} role="dialog" aria-label="Watch list">
        <div className={styles.header}>
          <h2>Watch List — Week {week}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className={styles.content}>
          {enrichedWatches.length === 0 ? (
            <p className={styles.empty}>No watched picks for this week.</p>
          ) : (
            enrichedWatches.map((pick) => (
              <PickItem
                key={pick.id}
                pick={{
                  ...pick,
                  status: pick.status || 'watched',
                }}
                onRemove={() => handleRemove(pick)}
                onLock={() => handleLock(pick)}
                showPadlockLock
                isDisabled={hasReachedMaxPicks}
                isFutureWeek={week > currentWeek}
                futureWeekLockAllowed={futureWeekLockAllowed}
              />
            ))
          )}
        </div>
        {watchesForWeek.length > 0 && (
          <div className={styles.footer}>
            <button type="button" className={styles.clearButton} onClick={handleClearAll}>
              Clear All Watches
            </button>
          </div>
        )}
      </div>
    </>
  );
};

MobileWatchList.propTypes = {
  week: PropTypes.number.isRequired,
  games: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MobileWatchList;
